import { useState, useEffect, useCallback } from "react";
import {
    Box, TableRow, TableCell, Typography, IconButton,
    Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Chip, Skeleton, Divider, Stack, Alert,
    Table, TableHead, TableBody, CircularProgress
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ComercialInterfaceHeader from "@/components/comercial/ComercialInterfaceHeader";
import ComercialDrawer from "@/components/pet-beauty/CormercialDrawer";
import ComercialTable from "@/components/comercial/ComercialTable";
import useManagerStores from "@/hooks/context-hooks/useManagerStores";
import { useTranslation } from "react-i18next";
import { sharedStyles } from "@/styles/shared";
import sellerGatewayApi from "@/services/api/SellerGateway";
import { useStoreGateways } from "@/hooks/entities/useStoreGateways";
import {
    Balance, BalanceModalProps, ReceivablesModalProps,
    TransactionsModalProps, StatementModalProps,
    SellerTransaction, StatementItem
} from "@/services/api/SellerGateway/types";
import { ReceivableDetail } from "@/services/api/Store/types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (value?: number) =>
    value != null
        ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        : "—";

const today = () => new Date().toISOString().split("T")[0];

const STATUS_COLOR: Record<string, "success" | "warning" | "error" | "default"> = {
    PAID: "success",
    PENDING: "warning",
    CANCELED: "error",
    REFUND: "warning",
    CHARGEBACK: "error",
    EXPIRED: "error",
};

// ─── Balance Modal ─────────────────────────────────────────────────────────────

const BalanceModal = ({ open, storeName, sellerId, onClose }: BalanceModalProps) => {
    const [balance, setBalance] = useState<Balance | null>(null);
    const [receivables, setReceivables] = useState<ReceivableDetail[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!sellerId) return;
        setLoading(true);
        setError(null);
        try {
            const [balanceRes, receivablesRes] = await Promise.all([
                sellerGatewayApi.findBalance(sellerId),
                sellerGatewayApi.findReceivableDetails(sellerId, today()),
            ]);
            setBalance(balanceRes.data);
            setReceivables(Array.isArray(receivablesRes.data) ? receivablesRes.data : []);
        } catch (e: any) {
            setError(e?.response?.data?.message || "Erro ao carregar dados financeiros.");
        } finally {
            setLoading(false);
        }
    }, [sellerId]);

    useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                    <Typography fontWeight={700}>Saldo & Extrato</Typography>
                    <Typography variant="caption" color="text.secondary">{storeName}</Typography>
                </Box>
                <Box>
                    <IconButton size="small" onClick={fetchData} disabled={loading} sx={{ mr: 0.5 }}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Typography variant="overline" color="text.secondary">Saldo disponível</Typography>
                {loading ? (
                    <Skeleton variant="text" width={160} height={40} />
                ) : (
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h5" fontWeight={700} color="success.main">
                            {formatCurrency(balance?.amount ?? 0)}
                        </Typography>
                        {balance?.blocked != null && (
                            <Chip
                                label={`Bloqueado: ${formatCurrency(balance.blocked)}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                            />
                        )}
                    </Stack>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" color="text.secondary">
                    Recebíveis — {today()}
                </Typography>
                {loading ? (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={36} />)}
                    </Stack>
                ) : receivables.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Nenhum recebível encontrado para hoje.
                    </Typography>
                ) : (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        {receivables.map((r, i) => (
                            <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
                                <Typography variant="body2">{r.date}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                                        {formatCurrency(r.totalAmount)}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} color="success.main">
                                        {formatCurrency(r.netAmount)}
                                    </Typography>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} size="small">Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Receivables Modal ────────────────────────────────────────────────────────

const ReceivablesModal = ({ open, storeName, sellerId, onClose }: ReceivablesModalProps) => {
    const [receivables, setReceivables] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!sellerId || sellerId === -1) return;
        setLoading(true);
        setError(null);
        try {
            const start = today();
            const end = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
            const res = await sellerGatewayApi.findFutureReceivables(Number(sellerId), start, end);
            setReceivables(Array.isArray(res.data) ? res.data : []);
        } catch (e: any) {
            setError(e?.response?.data?.message || "Erro ao buscar recebíveis futuros.");
        } finally {
            setLoading(false);
        }
    }, [sellerId]);

    useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                    <Typography fontWeight={700}>Recebíveis Futuros (30 dias)</Typography>
                    <Typography variant="caption" color="text.secondary">{storeName}</Typography>
                </Box>
                <Box>
                    <IconButton size="small" onClick={fetchData} disabled={loading} sx={{ mr: 0.5 }}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading ? (
                    <Stack spacing={1}>
                        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={36} />)}
                    </Stack>
                ) : receivables.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                        Nenhum recebível futuro encontrado.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {receivables.map((item, index) => (
                            <Box key={index} sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" fontWeight={600}>{item.date}</Typography>
                                    <Typography variant="body2" color="success.main" fontWeight={700}>
                                        {formatCurrency(item.netAmount)}
                                    </Typography>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Transactions Modal ───────────────────────────────────────────────────────

const TransactionsModal = ({ open, storeName, sellerId, onClose }: TransactionsModalProps) => {
    const [transactions, setTransactions] = useState<SellerTransaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchData = useCallback(async (p = 1) => {
        if (!sellerId) return;
        setLoading(true);
        setError(null);
        try {
            const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
            const end = today();
            const res = await sellerGatewayApi.findSellerTransactions(sellerId, {
                startDate: start,
                endDate: end,
                page: p,
                size: 10,
            });
            setTransactions(res.data?.content ?? []);
            setTotalPages(res.data?.totalPages ?? 1);
            setPage(p);
        } catch (e: any) {
            setError(e?.response?.data?.message || "Erro ao buscar transações.");
        } finally {
            setLoading(false);
        }
    }, [sellerId]);

    useEffect(() => { if (open) fetchData(1); }, [open, fetchData]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                    <Typography fontWeight={700}>Transações (últimos 30 dias)</Typography>
                    <Typography variant="caption" color="text.secondary">{storeName}</Typography>
                </Box>
                <Box>
                    <IconButton size="small" onClick={() => fetchData(page)} disabled={loading} sx={{ mr: 0.5 }}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading ? (
                    <Stack spacing={1}>
                        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rectangular" height={40} />)}
                    </Stack>
                ) : transactions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                        Nenhuma transação encontrada.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Título</TableCell>
                                <TableCell>Método</TableCell>
                                <TableCell align="right">Valor Bruto</TableCell>
                                <TableCell align="right">Valor Líquido</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell>Data</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.map((t) => (
                                <TableRow key={t.uuid}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{t.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">{t.installments}x</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{t.paymentMethod}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">{t.grossValue}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color="success.main" fontWeight={600}>{t.netValue}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={t.status}
                                            size="small"
                                            color={STATUS_COLOR[t.status] ?? "default"}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{t.paidDate}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {totalPages > 1 && (
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                        <Button size="small" disabled={page <= 1 || loading} onClick={() => fetchData(page - 1)}>Anterior</Button>
                        <Typography variant="body2">{page} / {totalPages}</Typography>
                        <Button size="small" disabled={page >= totalPages || loading} onClick={() => fetchData(page + 1)}>Próxima</Button>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Statement Modal ──────────────────────────────────────────────────────────

const StatementModal = ({ open, storeName, sellerId, onClose }: StatementModalProps) => {
    const [items, setItems] = useState<StatementItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!sellerId) return;
        setLoading(true);
        setError(null);
        try {
            const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
            const end = today();
            const res = await sellerGatewayApi.findStatement({
                idSeller: sellerId,
                executionDateInitial: start,
                executionDateFinal: end,
            });
            setItems(Array.isArray(res.data) ? res.data : []);
        } catch (e: any) {
            setError(e?.response?.data?.message || "Erro ao buscar extrato.");
        } finally {
            setLoading(false);
        }
    }, [sellerId]);

    useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                    <Typography fontWeight={700}>Extrato (últimos 30 dias)</Typography>
                    <Typography variant="caption" color="text.secondary">{storeName}</Typography>
                </Box>
                <Box>
                    <IconButton size="small" onClick={fetchData} disabled={loading} sx={{ mr: 0.5 }}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading ? (
                    <Stack spacing={1}>
                        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rectangular" height={40} />)}
                    </Stack>
                ) : items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                        Nenhum lançamento encontrado.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Operação</TableCell>
                                <TableCell>Método</TableCell>
                                <TableCell align="right">Entrada Líquida</TableCell>
                                <TableCell align="right">Saída Líquida</TableCell>
                                <TableCell>Taxa</TableCell>
                                <TableCell>Data</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.uuid}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{item.operacao}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.payment_method}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color="success.main" fontWeight={600}>
                                            {item.entrada_liquida > 0 ? formatCurrency(item.entrada_liquida) : "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color="error.main" fontWeight={600}>
                                            {item.saida_liquida > 0 ? formatCurrency(item.saida_liquida) : "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{item.taxa_operacao}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{item.execution_date}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const Finances = () => {
    const { t } = useTranslation();
    const { stores } = useManagerStores();
    const { gateways, isLoading } = useStoreGateways(stores);

    const [balanceModal, setBalanceModal] = useState<{ open: boolean; storeId: string; storeName: string } | null>(null);
    const [receivablesModal, setReceivablesModal] = useState<{ open: boolean; storeId: string; storeName: string } | null>(null);
    const [transactionsModal, setTransactionsModal] = useState<{ open: boolean; storeId: string; storeName: string } | null>(null);
    const [statementModal, setStatementModal] = useState<{ open: boolean; storeId: string; storeName: string } | null>(null);

    const getSellerId = (storeId: string) => gateways[storeId]?.idSeller ?? null;

    const financeTableHeader = (
        <TableRow>
            <TableCell align="left">{t("storePage.management.table.store")}</TableCell>
            <TableCell align="center">ID Seller (Gateway)</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="right">{t("storePage.management.table.actions")}</TableCell>
        </TableRow>
    );

    const financeTableBody = (
        <>
            {isLoading ? (
                [1, 2, 3].map((i) => (
                    <TableRow key={i}><TableCell colSpan={4}><Skeleton height={50} /></TableCell></TableRow>
                ))
            ) : stores?.length > 0 ? (
                stores.map((store) => {
                    const sg = gateways[store.id];
                    return (
                        <TableRow key={store.id}>
                            <TableCell align="left">
                                <Typography fontWeight={600}>{store.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{store.CNPJ}</Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography variant="body2" sx={{ fontFamily: "monospace", bgcolor: "action.selected", px: 1, borderRadius: 1 }}>
                                    {sg?.idSeller ?? "—"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={sg ? "Vinculado" : "Pendente"}
                                    size="small"
                                    color={sg ? "success" : "warning"}
                                    variant="outlined"
                                />
                            </TableCell>
                            <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Tooltip title="Saldo e Extrato">
                                        <span>
                                            <IconButton color="primary" disabled={!sg} onClick={() => setBalanceModal({ open: true, storeId: store.id, storeName: store.name })}>
                                                <AccountBalanceIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Recebíveis Futuros">
                                        <span>
                                            <IconButton color="secondary" disabled={!sg} onClick={() => setReceivablesModal({ open: true, storeId: store.id, storeName: store.name })}>
                                                <ReceiptLongIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Transações">
                                        <span>
                                            <IconButton color="info" disabled={!sg} onClick={() => setTransactionsModal({ open: true, storeId: store.id, storeName: store.name })}>
                                                <ListAltIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Extrato">
                                        <span>
                                            <IconButton color="warning" disabled={!sg} onClick={() => setStatementModal({ open: true, storeId: store.id, storeName: store.name })}>
                                                <SummarizeIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </TableCell>
                        </TableRow>
                    );
                })
            ) : (
                <TableRow>
                    <TableCell colSpan={4} align="center">
                        <Typography variant="body2" sx={{ py: 4 }}>Nenhuma loja encontrada.</Typography>
                    </TableCell>
                </TableRow>
            )}
        </>
    );

    return (
        <Box sx={sharedStyles.sharedMainContainer}>
            <ComercialDrawer content={
                <Box>
                    <ComercialInterfaceHeader title="Gestão Financeira" />
                    <Box sx={{ mt: 3 }}>
                        <ComercialTable header={financeTableHeader} body={financeTableBody} />
                    </Box>
                </Box>
            } />

            {balanceModal && (
                <BalanceModal
                    open={balanceModal.open}
                    storeName={balanceModal.storeName}
                    sellerId={getSellerId(balanceModal.storeId)}
                    onClose={() => setBalanceModal(null)}
                />
            )}
            {receivablesModal && (
                <ReceivablesModal
                    open={receivablesModal.open}
                    storeName={receivablesModal.storeName}
                    sellerId={getSellerId(receivablesModal.storeId)}
                    onClose={() => setReceivablesModal(null)}
                />
            )}
            {transactionsModal && (
                <TransactionsModal
                    open={transactionsModal.open}
                    storeName={transactionsModal.storeName}
                    sellerId={getSellerId(transactionsModal.storeId)}
                    onClose={() => setTransactionsModal(null)}
                />
            )}
            {statementModal && (
                <StatementModal
                    open={statementModal.open}
                    storeName={statementModal.storeName}
                    sellerId={getSellerId(statementModal.storeId)}
                    onClose={() => setStatementModal(null)}
                />
            )}
        </Box>
    );
};

export default Finances;